import React, { useImperativeHandle, forwardRef, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@cp/src/store';
import {
  Roles,
  agentCenterMandatoryRoles,
  agentStateAndLanguageEditRoles,
  emailMandatoryRoles
} from '@cp/src/pages/auth/constants/AuthConstants';
import { IOption } from '@cp/src/components/AutocompleteDropdown/interfaces';
import { Agent, AgentFormProps, AgentFormValues } from './interfaces/types';
import {
  getAgentPreferences,
  getAvailableRoles,
  getBucketGroup,
  getEditManagers,
  getIsEmailMandatoryForAgencyCode,
  getManagers,
  isValidString
} from './actions/agentFormActions';
import { defaultValues, NAVI_AGENCY_CODE, VERIFICATION_STATUS } from './constants/constants';
import {
  capitalizeFirstLetterOfEachWord,
  checkRolesForShowingPan,
  transformManagers,
  useAuthentication,
  validateEmail
} from './utils/utils';
import { createAgent, getAgentById, getAllAgents, updateAgent } from '../allAgentsAction';
import ActiveInactiveWrapper from './components/form/ActiveInactiveWrapper';
import LoaderIcon from '@cp/src/assets/icons/LoaderIcon';
import InactiveTextContainer from './components/form/InactiveTextContainer';
import styles from './AgentForm.module.scss';
import AgentRolesForm from './components/form/AgentRolesForm';
import SelectOptionsMessage from './components/form/SelectOptionsMessage';
import InternalFieldAgentForm from './components/form/InternalFieldAgentForm';
import ExternalFieldAgentForm from './components/form/ExternalFieldAgentForm';
import DefaultAgentForm from './components/form/DefaultAgentForm';
import { filtersParamsModifier, getFiltersQueryString, getQueryString } from '..';

export const AgentForm = forwardRef((props: AgentFormProps, ref) => {
  const {
    isDrawerOpen,
    onClose,
    isEditDisabled,
    setIsSubmitButtonDisabled,
    setIsDirtyField,
    showLoader,
    isEditable = false,
    setShowLoader,
    setShowActionButton
  } = props;

  const dispatch = useDispatch();
  const [isAgentActive, setIsAgentActive] = useState(true);
  const [isAgentActiveNextMonth, setIsAgentActiveNextMonth] = useState(true);
  const [isAgentActiveToggleDirty, setIsAgentActiveToggleDirty] = useState(false);
  const [isAgentActiveNextMonthToggleDirty, setIsAgentActiveNextMonthToggleDirty] = useState(false);
  const [agentData, setAgentData] = React.useState({} as Agent);
  const [showAgentForm, setShowAgentForm] = useState(false);
  const [showFieldAgentForm, setShowFieldAgentForm] = useState(false);
  const [showLocationVerificationToast, setShowLocationVerificationToast] = useState(false);
  const [locationVerificationStatus, setLocationVerificationStatus] = useState(
    VERIFICATION_STATUS.VERIFIED
  );

  const {
    handleSubmit,
    reset,
    control,
    formState: { errors, isDirty },
    getValues,
    setValue,
    watch,
    setFocus,
    register
  } = useForm<AgentFormValues>({
    defaultValues
  });

  const nextMonthLatitude = watch('nextMonthLatitude');
  const nextMonthLongitude = watch('nextMonthLongitude');
  const rolesAvailable = watch('roles');
  const agencyCodeRequested = watch('agencyCodeRequested');
  const isNaviAgencySelected = agencyCodeRequested === NAVI_AGENCY_CODE;
  const isOnlyFieldAgentRoleSelected =
    rolesAvailable.length === 1 && rolesAvailable[0] === Roles.ROLE_FIELD_AGENT;
  const isAgencyTeamLead =
    rolesAvailable.includes(Roles.ROLE_NAVI_FIELD_AGENCY_TEAM_LEAD) ||
    rolesAvailable.includes(Roles.ROLE_NAVI_FIELD_EXTERNAL_TEAM_LEAD) ||
    rolesAvailable.includes(Roles.ROLE_NAVI_16_30_FIELD_TEAM_LEAD);

  const hasRoleOMA = rolesAvailable.includes(Roles.ROLE_OMA);

  const showPanFields = checkRolesForShowingPan(rolesAvailable, isNaviAgencySelected);
  const isNaviAgencyAndFieldAgent =
    isNaviAgencySelected && rolesAvailable.includes(Roles.ROLE_FIELD_AGENT);
  const isPanFieldMandatory = !isAgencyTeamLead && !isNaviAgencyAndFieldAgent;

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      const isOnlyFieldAgentRoleOrIncludesRoleOma = isOnlyFieldAgentRoleSelected || hasRoleOMA;
      if (
        isOnlyFieldAgentRoleOrIncludesRoleOma &&
        (isValidString(nextMonthLatitude) || isValidString(nextMonthLongitude))
      ) {
        if (locationVerificationStatus === VERIFICATION_STATUS.VERIFIED) {
          handleSubmit(createOrUpdateAgent)();
        } else {
          setShowLocationVerificationToast(true);
        }
        return;
      }
      handleSubmit(createOrUpdateAgent)();
    }
  }));

  const [reverseGeocodedAgentCity, setReverseGeocodedAgentCity] = useState(
    agentData?.agentAllocationProfileView?.currentMonthAllocationView?.baseLocation?.city
  );
  const [reverseGeocodedAgentPincode, setReverseGeocodedAgentPincode] = useState(
    agentData?.agentAllocationProfileView?.currentMonthAllocationView?.baseLocation?.postalCode
  );

  const toggleAgentActive = () => {
    setIsAgentActive(prev => !prev);
    setIsAgentActiveToggleDirty(prev => !prev);
  };

  useEffect(() => {
    dispatch(getBucketGroup());
    dispatch(getAvailableRoles());
    dispatch(getIsEmailMandatoryForAgencyCode());
  }, []);

  const isEmailMandatory = useSelector(
    (state: RootState) => state.teamLeadDashboard.isEmailMandatory
  );
  const agentPreferences = useSelector(
    (state: RootState) => state.teamLeadDashboard.agentPreferences
  );
  const bucketGroup = useSelector((state: RootState) => state.teamLeadDashboard.bucketGroup);
  const availableRoles = useSelector((state: RootState) => state.teamLeadDashboard.availableRoles);
  const allAgentsPage = useSelector((state: RootState) => state.teamLeadDashboard.allAgentsPage);
  const agentRefId = useSelector((state: RootState) => state.teamLeadDashboard.agentRefId);

  const user = useSelector((state: RootState) => state.common.userData);
  const isPanVerified =
    agentData?.panCardView?.userDocument?.status === VERIFICATION_STATUS.APPROVED;
  const { rolesContainAnyOfV1 } = useAuthentication();
  const AVAILABLE_ROLES_LIST = availableRoles?.roles || [];
  const AGENCIES_REPORTING = user?.agenciesReporting || [];

  const [managerList, setManagerList] = useState<IOption[]>([]);
  const enableStateEdit = rolesContainAnyOfV1(agentStateAndLanguageEditRoles);
  const disableStateEdit = !!(!enableStateEdit && agentData?.referenceId);
  const isUserManagementRole = user?.roles?.includes(Roles.ROLE_USER_MANAGEMENT_ADMIN);

  const allowedRolesSet = new Set([
    Roles.ROLE_NAVI_EXTERNAL_FIELD_ZONAL_MANAGER,
    Roles.ROLE_NAVI_INHOUSE_FIELD_ZONAL_MANAGER,
    Roles.ROLE_COLLECTION_HEAD,
    Roles.ROLE_NAVI_EXTERNAL_FIELD_COLLECTION_HEAD
  ]);

  const hasPanEditAccess = user?.roles?.some(role => allowedRolesSet.has(role as Roles));
  const isDraUploadAccessFeatureFlagEnabled = useSelector(
    (store: RootState) => store.common.featureFlags?.draUploadAccess
  );

  useEffect(() => {
    if (rolesAvailable?.length && agencyCodeRequested) {
      if (isOnlyFieldAgentRoleSelected) {
        setShowFieldAgentForm(true);
      } else {
        setShowFieldAgentForm(false);
      }
      setShowAgentForm(true);
      setShowActionButton(true);
    } else {
      setShowAgentForm(false);
      setShowActionButton(false);
    }
  }, [rolesAvailable, agencyCodeRequested]);

  const getAgencyCode = () => {
    if (isEditable || isUserManagementRole) {
      return watch('agencyCodeRequested');
    }
    return user?.agencyCode;
  };

  const isNaviEmailRequired = getAgencyCode() === '1000';

  const isEmailRequired = (): boolean => {
    const agencyCodeUsed = getAgencyCode();
    const hasEmailMandatoryRole = watch('roles')?.some(role => emailMandatoryRoles.includes(role));

    return agencyCodeUsed === '1000' || hasEmailMandatoryRole;
  };

  const isAgentCenterRequired =
    watch('roles')?.some(role => agentCenterMandatoryRoles.includes(role)) &&
    user?.agencyCode === '1000';

  const isEmailChangeDisabled = isEditable && isEmailRequired() && agentData?.emailAddress !== null;

  const userAgencyCode = user?.agencyCode;
  const userRoles = user?.roles;
  const isExternalAgent = userAgencyCode !== NAVI_AGENCY_CODE;
  const isExternalTeamLead = isExternalAgent && userRoles?.includes(Roles.ROLE_TEAM_LEAD);
  const reportingAgency = user?.agencyCode;

  useEffect(() => {
    if (agentData?.referenceId) {
      const currentManager = managerList.find(
        manager => manager?.additionalData?.emailAddress === agentData?.reportingManagerEmailAddress
      );
      reset(
        {
          agentName: agentData?.name,
          roles: agentData?.roles?.sort(),
          phoneNumber: agentData?.phoneNumber,
          emailAddress: agentData?.emailAddress,
          active: String(!!agentData?.active),
          employeeId: agentData?.employeeId,
          reportingManagerEmailAddress: currentManager
            ? currentManager?.additionalData?.emailAddress
            : '',
          state: agentData?.state,
          primaryLanguage: agentData?.primaryLanguage,
          secondaryLanguage: agentData?.secondaryLanguage || '',
          tertiaryLanguage: agentData?.tertiaryLanguage || '',
          extraLanguages:
            agentData?.extraLanguages?.map(lang => capitalizeFirstLetterOfEachWord(lang)) || [],
          primaryRegion: agentData?.primaryRegion || '',
          secondaryRegion: agentData?.secondaryRegion || '',
          tertiaryRegion: agentData?.tertiaryRegion || '',
          bucketGroup: agentData?.bucketGroup,
          agencyCodeRequested: agentData?.agencyCodeRequested,
          agentCenter: agentData?.agentCenter,
          draCertificateView: agentData?.draCertificateView,
          draCertificate: {},
          dateOfBirth: agentData?.dateOfBirth,
          panNumber: agentData?.panNumber,
          panCardView: agentData?.panCardView,
          panCard: {},
          isTrainingAgent: agentData?.isTrainingAgent || false,
          currentMonthLatitude:
            agentData?.agentAllocationProfileView?.previousMonthAllocationView?.baseLocation
              ?.latitude,
          currentMonthLongitude:
            agentData?.agentAllocationProfileView?.previousMonthAllocationView?.baseLocation
              ?.longitude,
          nextMonthLatitude:
            agentData?.agentAllocationProfileView?.currentMonthAllocationView?.baseLocation
              ?.latitude,
          nextMonthLongitude:
            agentData?.agentAllocationProfileView?.currentMonthAllocationView?.baseLocation
              ?.longitude,
          agentCity:
            agentData?.agentAllocationProfileView?.currentMonthAllocationView?.metadata?.agentCity,
          topAgent:
            agentData?.agentAllocationProfileView?.currentMonthAllocationView?.metadata
              ?.agentPerformanceType,
          currentMonthAgentPincode:
            agentData?.agentAllocationProfileView?.previousMonthAllocationView?.metadata
              ?.agentPostalCode,
          nextMonthAgentPincode:
            agentData?.agentAllocationProfileView?.currentMonthAllocationView?.metadata
              ?.agentPostalCode,
          agentType:
            agentData?.agentAllocationProfileView?.currentMonthAllocationView?.metadata
              ?.agentBandwidthType,
          reverseGeocodedAgentCity:
            agentData?.agentAllocationProfileView?.currentMonthAllocationView?.baseLocation?.city,
          reverseGeocodedAgentPincode:
            agentData?.agentAllocationProfileView?.currentMonthAllocationView?.baseLocation
              ?.postalCode,
          activeNextMonth: String(
            !!agentData?.agentAllocationProfileView?.currentMonthAllocationView?.metadata?.active
          )
        },
        {
          keepDirty: !!isDrawerOpen
        }
      );
    } else {
      reset({
        ...defaultValues,
        agencyCodeRequested: isExternalTeamLead
          ? reportingAgency
          : defaultValues.agencyCodeRequested
      });
    }

    if (!isEmailMandatory) {
      dispatch(getIsEmailMandatoryForAgencyCode());
    }
    if (!agentPreferences) {
      dispatch(getAgentPreferences());
    }
    if (!bucketGroup) {
      dispatch(getBucketGroup());
    }
  }, [agentData, managerList, isDrawerOpen]);

  const refreshAgents = () => {
    const queryString = getQueryString();
    const filterQueryString = getFiltersQueryString();
    const filterPayload = { ...filterQueryString };
    const filtersPayload = filtersParamsModifier(filterPayload);
    return dispatch(getAllAgents(queryString, filtersPayload));
  };

  useEffect(() => {
    if (agentData?.active !== undefined) {
      setIsAgentActive(agentData?.active);
    }
    if (
      agentData?.agentAllocationProfileView?.currentMonthAllocationView?.metadata?.active !==
      undefined
    ) {
      setIsAgentActiveNextMonth(
        agentData?.agentAllocationProfileView?.currentMonthAllocationView?.metadata?.active?.toString() ===
          'true'
      );
    }
  }, [agentData]);

  const fetchManagers = async (refId?: string) => {
    if (!refId) return;
    const managers = await getManagers(refId);
    const transformedManagers = transformManagers(managers?.users);
    setManagerList(transformedManagers);
    setShowLoader(false);
  };

  const fetchEditManagers = async (refId?: string) => {
    if (!refId) return;
    const managers = await getEditManagers(refId);
    const transformedManagers = transformManagers(managers?.users);
    setManagerList(transformedManagers);
    setShowLoader(false);
  };

  const fetchAgentDataAndReportingManagers = async () => {
    const getAgentData = await getAgentById(agentRefId);
    setAgentData(getAgentData);
    setReverseGeocodedAgentCity(
      getAgentData?.agentAllocationProfileView?.currentMonthAllocationView?.baseLocation?.city
    );
    setReverseGeocodedAgentPincode(
      getAgentData?.agentAllocationProfileView?.currentMonthAllocationView?.baseLocation?.postalCode
    );
    await fetchEditManagers(getAgentData?.referenceId);
  };

  useEffect(() => {
    if (!isEditable) {
      fetchManagers(user?.referenceId);
      return;
    }
    if (!agentRefId) return;
    fetchAgentDataAndReportingManagers();
  }, [agentRefId]);

  const handleCreateAgentRequest = (data: AgentFormValues) => {
    const {
      agentName,
      phoneNumber,
      emailAddress,
      roles,
      employeeId,
      reportingManagerEmailAddress,
      state,
      primaryRegion,
      secondaryRegion,
      tertiaryRegion,
      primaryLanguage,
      secondaryLanguage,
      tertiaryLanguage,
      extraLanguages,
      bucketGroup,
      agencyCodeRequested,
      agentCenter,
      draCertificate,
      nextMonthLatitude,
      nextMonthLongitude,
      agentCity,
      topAgent,
      nextMonthAgentPincode,
      agentType,
      dateOfBirth,
      panNumber,
      panCard,
      isTrainingAgent
    } = data;
    const showTrainingAgentToggle =
      agencyCodeRequested === NAVI_AGENCY_CODE &&
      roles?.length === 1 &&
      roles?.[0] === Roles.ROLE_FIELD_AGENT;
    const createAgentRequest = {
      name: agentName,
      phoneNumber,
      emailAddress: emailAddress?.toLowerCase(),
      roles: roles,
      active: 'true',
      referenceId: null,
      reportingManagerEmailAddress,
      employeeId,
      state: state?.toUpperCase(),
      primaryRegion,
      secondaryRegion,
      tertiaryRegion,
      primaryLanguage: primaryLanguage?.toUpperCase(),
      secondaryLanguage: secondaryLanguage?.toUpperCase(),
      tertiaryLanguage: tertiaryLanguage?.toUpperCase(),
      extraLanguages: extraLanguages?.map(lang => lang?.toUpperCase()),
      bucketGroup,
      agencyCodeRequested,
      agentCenter: agentCenter?.toUpperCase(),
      reportingManagerReferenceId:
        managerList.find(
          manager => manager?.additionalData?.emailAddress === reportingManagerEmailAddress
        )?.additionalData?.referenceId || '',
      draCertificateView: {
        draCertificateUpdateRequest: {
          mode: 'ADD',
          uploadReferenceId: draCertificate?.referenceId || null
        }
      },
      dateOfBirth,
      panNumber: panNumber?.toUpperCase(),
      panCardView: {
        userDocumentUpdateRequest: {
          mode: 'ADD',
          uploadReferenceId: panCard?.referenceId || null
        }
      },
      agentAllocationProfileView: {
        currentMonthAllocationView: {
          metadata: {
            agentBandwidthType: agentType,
            agentPostalCode: nextMonthAgentPincode,
            agentCity: agentCity,
            agentPerformanceType: topAgent,
            active: 'true'
          },
          baseLocation: {
            latitude: nextMonthLatitude,
            longitude: nextMonthLongitude,
            city: reverseGeocodedAgentCity,
            postalCode: reverseGeocodedAgentPincode
          }
        }
      },
      isTrainingAgent: showTrainingAgentToggle && isTrainingAgent
    };
    createAgent(createAgentRequest, setIsSubmitButtonDisabled, setShowLoader, onClose);
  };

  const handleUpdateAgentRequest = (data: AgentFormValues) => {
    const {
      agentName,
      phoneNumber,
      emailAddress,
      roles,
      employeeId,
      reportingManagerEmailAddress,
      state,
      primaryRegion,
      secondaryRegion,
      tertiaryRegion,
      primaryLanguage,
      secondaryLanguage,
      tertiaryLanguage,
      extraLanguages,
      bucketGroup,
      agencyCodeRequested,
      agentCenter,
      draCertificate = {},
      draCertificateView = {},
      nextMonthLatitude,
      nextMonthLongitude,
      agentCity,
      topAgent,
      nextMonthAgentPincode,
      agentType,
      dateOfBirth,
      panNumber,
      panCard = {},
      panCardView = {},
      isTrainingAgent
    } = data;
    const userDocumentUpdateRequest =
      panCard && Object.keys(panCard).length > 0 ? panCard : panCardView?.userDocument ?? {};

    const draCertificateUpdateRequest =
      draCertificate && Object.keys(draCertificate).length > 0
        ? draCertificate
        : draCertificateView?.draCertificate ?? {};
    const showTrainingAgent =
      agencyCodeRequested === NAVI_AGENCY_CODE &&
      roles?.length === 1 &&
      roles?.[0] === Roles.ROLE_FIELD_AGENT;
    const updateAgentRequest = {
      name: agentName,
      phoneNumber,
      emailAddress: emailAddress?.toLowerCase(),
      roles: roles,
      active: isAgentActive?.toString(),
      referenceId: agentData?.referenceId,
      reportingManagerEmailAddress,
      employeeId,
      state: state?.toUpperCase(),
      primaryRegion,
      secondaryRegion,
      tertiaryRegion,
      primaryLanguage: primaryLanguage?.toUpperCase(),
      secondaryLanguage: secondaryLanguage?.toUpperCase(),
      tertiaryLanguage: tertiaryLanguage?.toUpperCase(),
      extraLanguages: extraLanguages?.map(lang => lang?.toUpperCase()),
      bucketGroup,
      agencyCodeRequested,
      agentCenter: agentCenter?.toUpperCase(),
      reportingManagerReferenceId:
        managerList.find(
          manager => manager?.additionalData?.emailAddress === reportingManagerEmailAddress
        )?.additionalData?.referenceId || '',
      draCertificateView: {
        draCertificateUpdateRequest:
          Object.keys(draCertificateUpdateRequest).length !== 0
            ? {
                mode: 'EDIT',
                uploadReferenceId: draCertificateUpdateRequest?.referenceId
              }
            : {
                mode: 'DELETE'
              }
      },
      dateOfBirth,
      panNumber: panNumber?.toUpperCase(),
      panCardView: {
        userDocumentUpdateRequest:
          Object.keys(userDocumentUpdateRequest)?.length !== 0
            ? {
                mode: 'EDIT',
                uploadReferenceId: userDocumentUpdateRequest?.referenceId
              }
            : {
                mode: 'DELETE'
              }
      },
      agentAllocationProfileView: {
        currentMonthAllocationView: {
          metadata: {
            agentPostalCode: nextMonthAgentPincode,
            agentBandwidthType: agentType,
            agentCity: agentCity,
            agentPerformanceType: topAgent,
            active: isAgentActiveNextMonth?.toString()
          },
          baseLocation: {
            latitude: nextMonthLatitude,
            longitude: nextMonthLongitude,
            city: reverseGeocodedAgentCity,
            postalCode: reverseGeocodedAgentPincode
          }
        }
      },
      isTrainingAgent: showTrainingAgent && isTrainingAgent
    };

    updateAgent(
      agentData?.referenceId as string,
      updateAgentRequest,
      setIsSubmitButtonDisabled,
      setShowLoader,
      onClose,
      refreshAgents
    );
  };

  const createOrUpdateAgent = (data: AgentFormValues) => {
    if (agentData?.referenceId && !isEditDisabled) {
      handleUpdateAgentRequest(data);
    } else if (!isEditDisabled) {
      handleCreateAgentRequest(data);
    }
  };

  useEffect(() => {
    setTimeout(() => {
      setFocus('agentName');
    }, 100);
  }, [isDrawerOpen]);

  useEffect(() => {
    if (typeof setIsDirtyField === 'function') {
      setIsDirtyField(isDirty || isAgentActiveToggleDirty || isAgentActiveNextMonthToggleDirty);
    }
  }, [isDirty, isAgentActiveToggleDirty, isAgentActiveNextMonthToggleDirty]);

  const showActiveNextMonthToggle =
    (showFieldAgentForm || rolesAvailable.includes(Roles.ROLE_IN_HOUSE_CSA_AGENT)) &&
    agentData?.referenceId;
  const isEditPanDetailsDisabled = isPanVerified && !hasPanEditAccess;

  return (
    <>
      <>
        {agentData?.referenceId && (
          <ActiveInactiveWrapper
            styles={styles}
            emailAddress={agentData?.emailAddress || ''}
            updatedAt={agentData?.updatedAt || ''}
            isAgentActive={isAgentActive}
            isEditDisabled={!!isEditDisabled}
            toggleAgentActive={toggleAgentActive}
            dashedLineColor="var(--navi-color-gray-c3)"
          />
        )}

        {agentData?.referenceId && !isAgentActive && <InactiveTextContainer />}
      </>

      <form onSubmit={handleSubmit(createOrUpdateAgent)}>
        <div className={styles.activeContainer} hidden={!isAgentActive}>
          <div>
            <AgentRolesForm
              isAgentActive={isAgentActive}
              control={control}
              errors={errors}
              isEditDisabled={isEditDisabled}
              AGENCIES_REPORTING={AGENCIES_REPORTING}
              setValue={setValue}
              AVAILABLE_ROLES_LIST={AVAILABLE_ROLES_LIST}
              isUserManagementRole={isUserManagementRole}
            />
            {showAgentForm ? (
              showFieldAgentForm ? (
                isNaviAgencySelected ? (
                  <InternalFieldAgentForm
                    control={control}
                    errors={errors}
                    isEditDisabled={isEditDisabled}
                    watch={watch}
                    isEmailRequired={isEmailRequired}
                    validateEmail={validateEmail}
                    isNaviEmailRequired={isNaviEmailRequired}
                    isEmailChangeDisabled={isEmailChangeDisabled}
                    agentData={agentData}
                    getValues={getValues}
                    getAgencyCode={getAgencyCode}
                    managerList={managerList}
                    setValue={setValue}
                    isDraUploadAccessFeatureFlagEnabled={isDraUploadAccessFeatureFlagEnabled}
                    setIsSubmitButtonDisabled={setIsSubmitButtonDisabled}
                    reverseGeocodedAgentCity={reverseGeocodedAgentCity}
                    reverseGeocodedAgentPincode={reverseGeocodedAgentPincode}
                    setReverseGeocodedAgentCity={setReverseGeocodedAgentCity}
                    setReverseGeocodedAgentPincode={setReverseGeocodedAgentPincode}
                    locationVerificationStatus={locationVerificationStatus}
                    setLocationVerificationStatus={setLocationVerificationStatus}
                    showLocationVerificationToast={showLocationVerificationToast}
                    setShowLocationVerificationToast={setShowLocationVerificationToast}
                    isEditPanDetailsDisabled={isEditPanDetailsDisabled}
                    showActiveNextMonthToggle={showActiveNextMonthToggle as boolean}
                    isAgentActiveNextMonth={isAgentActiveNextMonth}
                    setIsAgentActiveNextMonth={setIsAgentActiveNextMonth}
                    isAgentActiveNextMonthToggleDirty={isAgentActiveNextMonthToggleDirty}
                    setIsAgentActiveNextMonthToggleDirty={setIsAgentActiveNextMonthToggleDirty}
                    isPanFieldMandatory={isPanFieldMandatory}
                  />
                ) : (
                  <ExternalFieldAgentForm
                    control={control}
                    errors={errors}
                    isEditDisabled={isEditDisabled}
                    watch={watch}
                    isEmailRequired={isEmailRequired}
                    validateEmail={validateEmail}
                    isNaviEmailRequired={isNaviEmailRequired}
                    isEmailChangeDisabled={isEmailChangeDisabled}
                    agentData={agentData}
                    getValues={getValues}
                    getAgencyCode={getAgencyCode}
                    managerList={managerList}
                    setValue={setValue}
                    isDraUploadAccessFeatureFlagEnabled={isDraUploadAccessFeatureFlagEnabled}
                    setIsSubmitButtonDisabled={setIsSubmitButtonDisabled}
                    reverseGeocodedAgentCity={reverseGeocodedAgentCity}
                    reverseGeocodedAgentPincode={reverseGeocodedAgentPincode}
                    setReverseGeocodedAgentCity={setReverseGeocodedAgentCity}
                    setReverseGeocodedAgentPincode={setReverseGeocodedAgentPincode}
                    locationVerificationStatus={locationVerificationStatus}
                    setLocationVerificationStatus={setLocationVerificationStatus}
                    showLocationVerificationToast={showLocationVerificationToast}
                    setShowLocationVerificationToast={setShowLocationVerificationToast}
                    isEditPanDetailsDisabled={isEditPanDetailsDisabled}
                    showActiveNextMonthToggle={showActiveNextMonthToggle as boolean}
                    isAgentActiveNextMonth={isAgentActiveNextMonth}
                    setIsAgentActiveNextMonth={setIsAgentActiveNextMonth}
                    isAgentActiveNextMonthToggleDirty={isAgentActiveNextMonthToggleDirty}
                    setIsAgentActiveNextMonthToggleDirty={setIsAgentActiveNextMonthToggleDirty}
                  />
                )
              ) : (
                <DefaultAgentForm
                  control={control}
                  errors={errors}
                  isEditDisabled={isEditDisabled}
                  watch={watch}
                  isEmailRequired={isEmailRequired}
                  validateEmail={validateEmail}
                  isNaviEmailRequired={isNaviEmailRequired}
                  isEmailChangeDisabled={isEmailChangeDisabled}
                  agentData={agentData}
                  getValues={getValues}
                  getAgencyCode={getAgencyCode}
                  managerList={managerList}
                  setValue={setValue}
                  isDraUploadAccessFeatureFlagEnabled={isDraUploadAccessFeatureFlagEnabled}
                  setIsSubmitButtonDisabled={setIsSubmitButtonDisabled}
                  disableStateEdit={disableStateEdit}
                  isAgentCenterRequired={isAgentCenterRequired}
                  isEditPanDetailsDisabled={isEditPanDetailsDisabled}
                  isPanDetailsRequired={showPanFields}
                  isPanFieldMandatory={isPanFieldMandatory}
                  showActiveNextMonthToggle={showActiveNextMonthToggle as boolean}
                  isAgentActiveNextMonth={isAgentActiveNextMonth}
                  setIsAgentActiveNextMonth={setIsAgentActiveNextMonth}
                  isAgentActiveNextMonthToggleDirty={isAgentActiveNextMonthToggleDirty}
                  setIsAgentActiveNextMonthToggleDirty={setIsAgentActiveNextMonthToggleDirty}
                  hasRoleOMA={hasRoleOMA}
                  reverseGeocodedAgentCity={reverseGeocodedAgentCity}
                  reverseGeocodedAgentPincode={reverseGeocodedAgentPincode}
                  setReverseGeocodedAgentCity={setReverseGeocodedAgentCity}
                  setReverseGeocodedAgentPincode={setReverseGeocodedAgentPincode}
                  locationVerificationStatus={locationVerificationStatus}
                  setLocationVerificationStatus={setLocationVerificationStatus}
                  showLocationVerificationToast={showLocationVerificationToast}
                  setShowLocationVerificationToast={setShowLocationVerificationToast}
                />
              )
            ) : (
              <SelectOptionsMessage />
            )}
          </div>
        </div>
      </form>
      {showLoader ? (
        <div className="absolute h-full w-full bg-white top-0 flex items-center justify-center">
          <LoaderIcon
            size={40}
            borderWidth={4}
            borderColor="var(--navi-color-gray-border)"
            fillColor="var(--navi-color-blue-base)"
          />
        </div>
      ) : null}
    </>
  );
});
